import {useEffect, useState} from "react";
import {useTheme} from "@mui/material/styles";
import {initTrixControllers, disableEditing, enableEditing} from "./trixEditorControllers";

const editableSelector = "[data-editable]";  // css editable selector that identify all the editable tags

const TrixEditor = (props) => {
    const {
        iframeRef,
        iframeLoaded = false,
        editEnabled = false,
        onTemplateUserVariableChange = f => f,
        children
    } = props;

    const [editorDocument, setEditorDocument] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const theme = useTheme();

    useEffect(
        () => {
            if (iframeLoaded && !isInitialized && editEnabled) {
                initializeEditor(iframeRef?.current?.contentDocument, iframeRef?.current?.contentWindow);
            }
            if (isInitialized && editEnabled) {
                enableEditing(editorDocument, editableSelector);
            }
            if (isInitialized && !editEnabled) {
                disableEditing(editorDocument, editableSelector);
            }
        },
        [editEnabled, isInitialized, iframeRef, iframeLoaded] // eslint-disable-line react-hooks/exhaustive-deps
    )

    const initializeEditor = (editorDocument, editorWindow) => {
        if (!isInitialized && !!editorDocument && !!editorWindow) {
            setEditorDocument(editorDocument);

            // Load trix
            const scriptLoader = editorDocument.createElement("script");
            scriptLoader.setAttribute("src", `https://unpkg.com/trix@2.0.8/dist/trix.umd.min.js`);
            editorDocument.body.appendChild(scriptLoader);
            const trixStyle = editorDocument.createElement("link");
            trixStyle.setAttribute("rel", "stylesheet");
            trixStyle.setAttribute("href", `https://unpkg.com/trix@2.0.8/dist/trix.css`);
            editorDocument.head.appendChild(trixStyle);

            // Add listener to initialize the trix editor
            editorWindow.addEventListener("trix-initialize", (event) => {
                initTrixControllers(event, editorWindow.Trix, editableSelector);
            })

            // Add listener to any change in the editors
            editorWindow.addEventListener("trix-change", (event) => {
                const editorElement = event.target;
                const editable = editorElement.closest(editableSelector);
                const input = editable.querySelector('input');
                onTemplateUserVariableChange({[editable.dataset.editable]: input.value});
            });

            // Add style for the editing mode
            const style = editorDocument.createElement("style");
            style.innerHTML = `
                trix-editor {
                    display: inline-block;
                    min-height: 12px;
                    padding: 2px;
                    min-width: 2rem;
                    border: 0;
                    outline: 1px dotted ${theme.palette.primary.main};
                }
        
                trix-editor:focus {
                    outline: 1px solid ${theme.palette.primary.main};
                }
        
                trix-toolbar {
                    z-index: 0;
                    background-color: white;
                    position: absolute;
                    bottom: 100%;
                    display: none;
                    margin-bottom: 8px;
                }
                
                @media (max-width: 768px) {
                    trix-toolbar {
                        display: none !important;
                    }
                }
        
                .editor-container {
                    position: relative;
                    display: inline-block;
                }
        
                trix-toolbar.active {
                    z-index: 1;
                    display: block;
                }
        
                .trix-color-button svg{
                    margin-left: 6px;
                    margin-right: 6px;
                }
        
                .trix-dialog {
                    border-top: 0 !important;
                }
                .trix-dialog--color{
                    width: 240px;
                    display:none
                }
                .trix-button--icon-color::before{
                    background-image:url(data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%3E%3Cpath%20d%3D%22m16.56%2011.94-8.94-8.94-1.41%201.41%202.38%202.38-5.15%205.15c-.59.59-.59%201.54%200%202.12l5.5%205.5c.29.29.68.44%201.06.44s.77-.15%201.06-.44l5.5-5.5c.59-.58.59-1.53%200-2.12zm-11.35%201.06%204.79-4.79%204.79%204.79zm13.79%202.5s-2%202.17-2%203.5c0%201.1.9%202%202%202s2-.9%202-2c0-1.33-2-3.5-2-3.5z%22%2F%3E%3C%2Fsvg%3E)
                }
                .color-picker{
                    grid-template-columns:repeat(9, 1fr);
                    grid-gap:4px;
                    display:grid
                }
                .color-picker:first-of-type{
                    margin-bottom:8px
                }
                .color-picker__field{
                    position:relative;
                    min-width:1em;
                    min-height:1em;
                    aspect-ratio:1/1;
                    cursor:pointer;
                    text-indent:-999em
                }
                .color-picker__input{
                    position:absolute;
                    top:0;
                    left:0;
                    z-index:-1;
                    opacity:0
                }
                .color-picker__input:checked~.color-picker__swatch::before{
                    content:"✓";
                    font-weight:bold
                }
                .color-picker__swatch{
                    position:absolute;
                    top:0;
                    right:0;
                    bottom:0;
                    left:0;
                    text-align:center;
                    border:1px solid rgba(0,0,0,0);
                    border-radius:4px;
                    display:flex;
                    align-items:center;
                    justify-content:center;
                    text-indent:initial
                }
                .color-picker--for-text .color-picker__swatch::before{
                    content:"Ab";
                    color:currentColor
                }
                .color-picker--for-text .color-picker__swatch::before{
                    content:"Ab";
                    color:currentColor
                }
                .color-picker--for-text-foreground .color-picker__swatch{
                    background-color:var(--color-bg--message-content) !important
                }
                .color-picker--for-text-background .color-picker__swatch{
                    color:var(--color-txt--on-message-content) !important
                }
                .color-picker-remove-button{
                    background-color:transparent;
                    border:0;
                    color:var(--color-txt--on-message-content);
                    cursor:pointer;
                    font-size:inherit;
                    padding: 8px 0;
                    margin-top: 8px;
                    text-decoration:underline
                }
                .color_dialog__hide-unless-selection-contains-coloring{
                    display:none
                }
                .trix-dialog--color.color_dialog--selection-contains-coloring .color_dialog__hide-unless-selection-contains-coloring{
                    display:block
                }
                trix-toolbar .trix-button-row {
                    border-radius: 3rem;
                    display: flex;
                    flex-wrap: nowrap;
                    justify-content: space-between;
                    overflow-x: auto;
                    background: rgb(251, 250, 249);
                    box-shadow: 0px 3px 3px -2px rgba(0,0,0,0.2), 0px 3px 4px 0px rgba(0,0,0,0.14), 0px 1px 8px 0px rgba(0,0,0,0.12);
                }
                .trix-button-group, .trix-button {
                    border: 0 !important;
                    margin: 0 !important;
                    padding: .5rem;
                }
            `
            editorDocument.head.appendChild(style);

            setIsInitialized(true);
        }
    }

    return children;
}

export default TrixEditor;